/**
 * The customer portal's Quotes section — one quote, read only (item 7b).
 *
 * There is no share, edit, cancel, delete or "add option" control anywhere on
 * this page, and none is hidden behind a flag either. The server refuses those
 * writes to a customer independently
 * (`backend/tests/test_customer_portal_quotes.py`), so the two agree instead of
 * the UI being the only thing standing in the way.
 *
 * Two requests back it:
 *   GET /api/v1/quotes/requests/{id}/   — the request itself
 *   GET /api/v1/quotes/quotes/?quote_request={id} — the priced insurer options
 * Both are scoped server-side to this customer, by BOTH the login FK and the
 * contact-record FK (item 7's backend half). A quote that is not theirs 404s
 * here rather than rendering.
 *
 * Attachments are deliberately not offered: `QuoteDocumentViewSet` scopes by
 * broker, so a customer login can reach none of them and a download button
 * would be a dead one. The broker's shared link is the route to the PDF.
 */

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Badge, BackButton, Card, CardHeader, EmptyState, getStatusBadgeVariant } from '@/components/common';
import { PageHeader } from '@/components/layout';
import { useQuote } from '@/hooks';
import { quoteService } from '@/services/quotes';
import { formatCurrency, formatDate, formatDateTime, titleCase } from '@/utils';

import { SharedStatusBadge, type QuoteRow } from './quotesPending';

export function MyQuoteDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuote(id);
  const quote = data as QuoteRow | undefined;

  const options = useQuery({
    queryKey: ['my-quote-options', id],
    enabled: !!id,
    queryFn: () => quoteService.getQuoteOptions(id),
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="my-quote-detail-skeleton">
        <div className="h-8 w-64 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-40 rounded bg-gray-100 dark:bg-gray-700" />
        <div className="h-40 rounded bg-gray-100 dark:bg-gray-700" />
      </div>
    );
  }

  if (isError || !quote) {
    // Not "no such quote". We do not know that; we know the request failed or
    // was refused. Saying otherwise is the failure this codebase keeps making.
    return (
      <div>
        <BackButton to="/my-quotes" label="Back to My Quotes" />
        <EmptyState
          title="We could not load this quote"
          description="It may not be available to you, or something went wrong reaching your broker. Please go back and try again."
        />
      </div>
    );
  }

  return (
    <div>
      <BackButton to="/my-quotes" label="Back to My Quotes" />
      <PageHeader
        title={quote.request_number}
        subtitle={`${quote.insurance_type_name} · ${titleCase(quote.business_type)}`}
        breadcrumbs={[{ label: 'My Quotes', href: '/my-quotes' }, { label: quote.request_number }]}
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Badge variant={getStatusBadgeVariant(quote.status)}>{titleCase(quote.status)}</Badge>
        <SharedStatusBadge isShared={quote.is_shared} />
      </div>

      <Card className="mb-6">
        <CardHeader title="Quote details" />
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Quote number" value={quote.request_number} />
          <Field label="Insurance" value={quote.insurance_type_name} />
          <Field label="Business type" value={titleCase(quote.business_type)} />
          <Field label="Prepared by" value={quote.broker_name} />
          <Field label="Requested on" value={formatDateTime(quote.created_at)} />
          <Field label="Proposer" value={quote.proposer_name ?? quote.customer_name ?? '—'} />
        </dl>
      </Card>

      <Card padding="none">
        <div className="px-6 pt-6">
          <CardHeader title="Insurer options" subtitle="The prices your broker has put together for this quote." />
        </div>
        {options.isLoading ? (
          <div className="animate-pulse space-y-3 px-6 pb-6" data-testid="my-quote-options-skeleton">
            <div className="h-14 rounded bg-gray-100 dark:bg-gray-700" />
            <div className="h-14 rounded bg-gray-100 dark:bg-gray-700" />
          </div>
        ) : options.isError ? (
          <EmptyState
            title="We could not load the prices"
            description="The quote itself is above. Please try again in a moment."
          />
        ) : (options.data ?? []).length === 0 ? (
          <EmptyState
            title="No prices yet"
            description="Your broker has not added any insurer options to this quote yet."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Insurer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Premium
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Cover period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(options.data ?? []).map((option) => (
                  <tr key={option.id}>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {option.insurer_name || 'Insurer'}
                      </p>
                      {option.product_name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{option.product_name}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {option.total_premium === null || option.total_premium === undefined
                        ? '—'
                        : formatCurrency(option.total_premium)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {option.cover_start && option.cover_end
                        ? `${formatDate(option.cover_start)} – ${formatDate(option.cover_end)}`
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusBadgeVariant(option.status)} size="sm">
                        {option.status_display || titleCase(option.status)}
                      </Badge>
                      {option.issued_policy_number && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Policy {option.issued_policy_number}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{value || '—'}</dd>
    </div>
  );
}
